/**
 * Admin: WhatsApp integration status + test send.
 * Route: /admin/whatsapp
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, CheckCircle2, XCircle, RefreshCw, AlertTriangle, Send, Copy, ExternalLink, Phone, Key, Webhook, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AdminLayout from "@/components/app/AdminLayout";
import api from "@/lib/api";

export default function WhatsApp() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [templates, setTemplates] = useState([]);

  // Test-send form
  const [to, setTo] = useState("");
  const [template, setTemplate] = useState("");
  const [language, setLanguage] = useState("en_US");
  const [bodyParams, setBodyParams] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, m, t] = await Promise.all([
        api.get("/admin/whatsapp/status"),
        api.get("/admin/whatsapp/messages?limit=20"),
        api.get("/admin/whatsapp/templates").catch(() => ({ data: [] })),
      ]);
      setStatus(s.data);
      setMessages(Array.isArray(m.data) ? m.data : []);
      const tmpls = Array.isArray(t.data) ? t.data : [];
      setTemplates(tmpls);
      // Auto-select first approved template
      if (tmpls.length && !template) {
        setTemplate(tmpls[0].name);
        setLanguage(tmpls[0].language || "en_US");
      }
    } catch (_) {
      toast.error("שגיאה בטעינת סטטוס WhatsApp");
    } finally {
      setLoading(false);
    }
  }, [template]);

  useEffect(() => { load(); }, [load]);

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} הועתק`);
    } catch (_) {
      toast.error("ההעתקה נכשלה");
    }
  };

  const sendTest = async (e) => {
    e.preventDefault();
    if (!to.trim() || !template.trim()) {
      toast.error("חסר מספר יעד או שם template");
      return;
    }
    setSending(true);
    try {
      const params = bodyParams.trim() ? bodyParams.split("|").map((s) => s.trim()) : [];
      const { data } = await api.post("/admin/whatsapp/test-send", {
        to: to.trim(),
        template: template.trim(),
        language: language.trim(),
        body_params: params,
      });
      toast.success(`נשלח! ID: ${data.message_id?.slice(-12)}`);
      setTo("");
      setBodyParams("");
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.whatsapp_error) {
        toast.error(`WhatsApp: ${detail.whatsapp_error.message || detail.whatsapp_error.error_user_msg || "שליחה נכשלה"}`);
      } else {
        toast.error(typeof detail === "string" ? detail : "שליחה נכשלה");
      }
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <AdminLayout title="WhatsApp"><div className="p-12 text-center text-stone-400 text-sm">טוען...</div></AdminLayout>;
  }

  const cfg = status?.configured || {};
  const phone = status?.phone_number || {};
  const tokenDebug = status?.token_debug || {};
  const tokenExpiresAt = tokenDebug.expires_at ? new Date(tokenDebug.expires_at * 1000) : null;
  const daysUntilExpiry = tokenExpiresAt ? Math.round((tokenExpiresAt - Date.now()) / 86400000) : null;
  const isExpiringSoon = daysUntilExpiry !== null && daysUntilExpiry < 14;

  return (
    <AdminLayout title="WhatsApp — חיבור וטריגרים">
      {/* Connection status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-2xl border border-stone-200 p-5 shadow-sm" data-testid="wa-phone-card">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
              <Phone className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-[800] text-brand-charcoal text-sm">מספר השולח</h3>
              <p className="text-xs text-stone-500">Meta WhatsApp Cloud API</p>
            </div>
            <Button variant="ghost" size="sm" onClick={load} className="h-8 w-8 p-0"><RefreshCw className="w-3.5 h-3.5" /></Button>
          </div>
          <div className="space-y-2 text-sm">
            <Row label="שם מאומת" value={phone.verified_name || "—"} testId="wa-verified-name" />
            <Row label="מספר" value={phone.display_phone_number || "—"} ltr />
            <Row label="Platform" value={phone.platform_type || "—"} />
            <Row label="Quality Rating" value={phone.quality_rating || "—"} pill={qualityColor(phone.quality_rating)} />
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-stone-200 p-5 shadow-sm" data-testid="wa-token-card">
          <div className="flex items-start gap-3 mb-4">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isExpiringSoon ? "bg-amber-50" : "bg-blue-50"}`}>
              <Key className={`w-5 h-5 ${isExpiringSoon ? "text-amber-600" : "text-blue-600"}`} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-[800] text-brand-charcoal text-sm">Access Token</h3>
              <p className="text-xs text-stone-500">משמש לחתימת בקשות ל-Meta Graph</p>
            </div>
          </div>
          <div className="space-y-2 text-sm">
            <Row label="תקף?" value={
              tokenDebug.is_valid ? (
                <span className="inline-flex items-center gap-1 text-emerald-700 font-bold"><CheckCircle2 className="w-4 h-4" />כן</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-rose-700 font-bold"><XCircle className="w-4 h-4" />לא</span>
              )
            } />
            <Row label="פוקע בעוד" value={
              daysUntilExpiry === null ? "—" : (
                <span className={`font-bold ${isExpiringSoon ? "text-amber-700" : "text-emerald-700"}`}>
                  {daysUntilExpiry <= 0 ? "פג!" : `${daysUntilExpiry} ימים`}
                </span>
              )
            } />
            <Row label="Scopes" value={
              <span className="text-xs font-mono text-stone-600 ltr text-left break-all">
                {(tokenDebug.scopes || []).join(", ") || "—"}
              </span>
            } />
          </div>
          {isExpiringSoon && (
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900 leading-[1.6]" data-testid="wa-token-warning">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>הטוקן פג בקרוב.</strong> ל-Production חובה טוקן קבוע (System User Token). ניגש ל-
                  <a href="https://business.facebook.com/settings/system-users" target="_blank" rel="noreferrer" className="underline mx-1 inline-flex items-center gap-0.5">
                    Business Settings → System Users<ExternalLink className="w-3 h-3" />
                  </a>
                  → הענק לו scope <code className="bg-white px-1 rounded ltr">whatsapp_business_messaging</code> → הפק Never-expires Token.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Configuration overview */}
      <div className="bg-white rounded-2xl border border-stone-200 p-5 shadow-sm mb-6" data-testid="wa-cfg-card">
        <h3 className="font-[800] text-brand-charcoal text-sm mb-4">הגדרות שנטענו מ-.env</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <CfgBadge label="App ID" ok={cfg.app_id} />
          <CfgBadge label="App Secret" ok={cfg.app_secret} />
          <CfgBadge label="Access Token" ok={cfg.access_token} />
          <CfgBadge label="Phone Number ID" ok={cfg.phone_number_id} />
          <CfgBadge label="WABA ID" ok={cfg.waba_id} />
          <CfgBadge label="Webhook Verify Token" ok={cfg.verify_token} />
        </div>

        {status?.webhook_url && (
          <div className="mt-5 pt-5 border-t border-stone-100">
            <h4 className="font-[800] text-sm text-brand-charcoal mb-2 flex items-center gap-2">
              <Webhook className="w-4 h-4 text-brand-amber" /> הגדרת Webhook ב-Meta App
            </h4>
            <p className="text-xs text-stone-500 mb-3 leading-[1.6]">
              ב-Meta Developer Console → WhatsApp → Configuration → Webhook: הזן את הכתובת ואת ה-Verify Token, ואחר כך הירשם ל-events של <code className="bg-stone-100 px-1 rounded ltr">messages</code>.
            </p>
            <div className="space-y-2">
              <CopyRow label="Callback URL" value={status.webhook_url} onCopy={() => copyToClipboard(status.webhook_url, "Callback URL")} />
              <CopyRow label="Verify Token" value={status.webhook_verify_token || "—"} onCopy={() => copyToClipboard(status.webhook_verify_token || "", "Verify Token")} />
            </div>
          </div>
        )}
      </div>

      {/* Test send */}
      <div className="bg-white rounded-2xl border border-stone-200 p-5 shadow-sm mb-6" data-testid="wa-test-send-card">
        <h3 className="font-[800] text-brand-charcoal text-sm mb-1 flex items-center gap-2">
          <Send className="w-4 h-4 text-brand-amber" /> שליחת בדיקה
        </h3>
        <p className="text-xs text-stone-500 mb-4 leading-[1.6]">
          שליחת <strong>template message</strong> — הודעות שמפעילות שיחה חייבות להיות template מאושר על WABA שלך.
          {templates.length > 0 && (
            <> נמצאו <strong>{templates.length}</strong> templates מאושרים.</>
          )}
        </p>
        <form onSubmit={sendTest} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="wa-test-send-form">
          <div>
            <Label htmlFor="wa-to" className="text-sm font-bold text-stone-700 mb-1.5 block">מספר יעד <span className="text-rose-500">*</span></Label>
            <Input id="wa-to" data-testid="wa-to" value={to} onChange={(e) => setTo(e.target.value)} placeholder="+972501234567 או 0501234567" dir="ltr" className="h-11 ltr" required />
            <p className="text-xs text-stone-500 mt-1">מספר ישראלי (05...) יומר אוטומטית ל-972</p>
          </div>
          <div>
            <Label htmlFor="wa-template" className="text-sm font-bold text-stone-700 mb-1.5 block">שם Template <span className="text-rose-500">*</span></Label>
            {templates.length > 0 ? (
              <select
                id="wa-template"
                data-testid="wa-template"
                value={template}
                onChange={(e) => {
                  const t = templates.find((x) => x.name === e.target.value);
                  setTemplate(e.target.value);
                  if (t?.language) setLanguage(t.language);
                }}
                dir="ltr"
                className="ltr w-full h-11 px-3 border border-stone-200 rounded-md text-left bg-white font-mono text-sm"
                required
              >
                {templates.map((t) => (
                  <option key={`${t.name}_${t.language}`} value={t.name}>
                    {t.name} · {t.language} · {t.category}
                  </option>
                ))}
              </select>
            ) : (
              <>
                <Input id="wa-template" data-testid="wa-template" value={template} onChange={(e) => setTemplate(e.target.value)} placeholder="hello_world" dir="ltr" className="h-11 ltr" required />
                <p className="text-xs text-amber-700 mt-1">אין templates מאושרים עדיין. אשר תבנית ב-<a href="https://business.facebook.com/wa/manage/message-templates" target="_blank" rel="noreferrer" className="underline">WhatsApp Manager</a> ואז חזור לכאן.</p>
              </>
            )}
          </div>
          <div>
            <Label htmlFor="wa-lang" className="text-sm font-bold text-stone-700 mb-1.5 block">שפה</Label>
            <Input id="wa-lang" data-testid="wa-lang" value={language} onChange={(e) => setLanguage(e.target.value)} placeholder="en_US" dir="ltr" className="h-11 ltr" readOnly={templates.length > 0} />
            <p className="text-xs text-stone-500 mt-1">נטען אוטומטית מה-template שנבחר</p>
          </div>
          <div>
            <Label htmlFor="wa-params" className="text-sm font-bold text-stone-700 mb-1.5 block">משתני body (מופרדים ב-|)</Label>
            <Input id="wa-params" data-testid="wa-params" value={bodyParams} onChange={(e) => setBodyParams(e.target.value)} placeholder="ישראל | https://example.com/link" className="h-11" />
            <p className="text-xs text-stone-500 mt-1">רק אם ה-template כולל {"{{1}}, {{2}}"} וכו׳</p>
          </div>
          <div className="sm:col-span-2 flex justify-end">
            <Button type="submit" disabled={sending} data-testid="wa-test-send-btn" className="h-11 px-6 rounded-full bg-brand-amber hover:bg-brand-amberHover text-white font-bold">
              <Send className="w-4 h-4 ms-1" /> {sending ? "שולח..." : "שלח"}
            </Button>
          </div>
        </form>
      </div>

      {/* Recent messages */}
      <div className="bg-white rounded-2xl border border-stone-200 p-5 shadow-sm" data-testid="wa-msgs-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-[800] text-brand-charcoal text-sm flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-brand-amber" /> הודעות אחרונות
          </h3>
          <span className="text-xs text-stone-500 font-bold">{messages.length}</span>
        </div>
        {messages.length === 0 ? (
          <p className="text-center text-stone-400 text-sm py-8">אין עדיין הודעות. שלחו הודעת בדיקה למעלה.</p>
        ) : (
          <ul className="divide-y divide-stone-100">
            {messages.map((m) => (
              <li key={m.id} className="py-3 flex items-center gap-3 text-sm" data-testid={`wa-msg-row-${m.id}`}>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${statusColor(m.status)}`}>
                  {m.status}
                </span>
                <span className="font-bold text-brand-charcoal">{m.direction === "outbound" ? "→" : "←"} {m.to || m.from || "—"}</span>
                <span className="text-stone-500 text-xs truncate flex-1" title={m.body || m.template_name}>
                  {m.body || (m.template_name && `Template: ${m.template_name}`) || m.channel || "—"}
                </span>
                <span className="text-[10px] text-stone-400 font-mono flex items-center gap-1"><Clock className="w-3 h-3" />{m.created_at?.slice(11, 16)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </AdminLayout>
  );
}

function Row({ label, value, ltr, testId }) {
  return (
    <div className="flex items-center justify-between gap-2 text-sm border-b border-stone-50 pb-1.5 last:border-0" data-testid={testId}>
      <span className="text-stone-500 text-xs">{label}</span>
      <span className={`font-bold text-brand-charcoal ${ltr ? "ltr text-left" : ""}`}>{value}</span>
    </div>
  );
}

function CfgBadge({ label, ok }) {
  return (
    <div className={`flex items-center gap-2 rounded-xl px-3 py-2 border ${ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-200 text-rose-700"}`}>
      {ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
      <span className="text-xs font-bold">{label}</span>
    </div>
  );
}

function CopyRow({ label, value, onCopy }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-xs font-mono ltr text-left break-all">
        <div className="text-[10px] text-stone-500 uppercase tracking-wide font-bold mb-0.5" dir="rtl">{label}</div>
        {value}
      </div>
      <button type="button" onClick={onCopy} className="h-9 w-9 rounded-lg bg-stone-100 hover:bg-brand-amber hover:text-white flex items-center justify-center transition" title="העתק">
        <Copy className="w-4 h-4" />
      </button>
    </div>
  );
}

function statusColor(s) {
  switch (s) {
    case "delivered": return "bg-emerald-100 text-emerald-800";
    case "read": return "bg-blue-100 text-blue-800";
    case "sent": return "bg-stone-100 text-stone-700";
    case "failed": return "bg-rose-100 text-rose-800";
    case "received": return "bg-amber-100 text-amber-800";
    default: return "bg-stone-100 text-stone-600";
  }
}

function qualityColor(q) {
  if (q === "GREEN") return "bg-emerald-100 text-emerald-800";
  if (q === "YELLOW") return "bg-amber-100 text-amber-800";
  if (q === "RED") return "bg-rose-100 text-rose-800";
  return "bg-stone-100 text-stone-600";
}
